import { useEffect, useState } from "react";

const SPATIAL_INTELLIGENCE_STAGES = [
  { percent: 0, label: "Perceptual Blindness" },
  { percent: 25, label: "Edges & Gradients Recognized" },
  { percent: 50, label: "Volumetric Masses Discovered" },
  { percent: 75, label: "Causal & Object Relationships Formed" },
  { percent: 100, label: "Full Spatial Intelligence" },
];

export function SceneBlurSlider({ value = 0, onChange, isCollapsed: externallyCollapsed = false }) {
  const normalizedValue = Math.max(0, Math.min(100, Number(value) || 0));
  const spatialStage = [...SPATIAL_INTELLIGENCE_STAGES]
    .reverse()
    .find((stage) => normalizedValue >= stage.percent);
  const spatialStageWords = spatialStage.label.split(" ");
  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const isCollapsed = externallyCollapsed || isManuallyCollapsed;

  useEffect(() => {
    setShowSwipeHint(true);
  }, []);

  const dismissSwipeHint = () => setShowSwipeHint(false);

  const updateFromPointer = (event) => {
    dismissSwipeHint();
    const track = event.currentTarget.parentElement;
    const rect = track.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    onChange?.(Math.round((1 - position) * 100));
  };

  const handleMetricPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromPointer(event);
  };

  const handleMetricPointerMove = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      updateFromPointer(event);
    }
  };

  const handleMetricPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleMetricKeyDown = (event) => {
    const step = event.shiftKey ? 10 : 1;
    let nextValue = normalizedValue;

    if (event.key === "ArrowUp" || event.key === "PageUp") {
      nextValue = Math.min(100, normalizedValue + (event.key === "PageUp" ? 10 : step));
    } else if (event.key === "ArrowDown" || event.key === "PageDown") {
      nextValue = Math.max(0, normalizedValue - (event.key === "PageDown" ? 10 : step));
    } else if (event.key === "Home") {
      nextValue = 0;
    } else if (event.key === "End") {
      nextValue = 100;
    } else {
      return;
    }

    event.preventDefault();
    dismissSwipeHint();
    onChange?.(nextValue);
  };

  return (
    <div
      className={`scene-blur-control${isCollapsed ? " is-collapsed" : ""}`}
      aria-label="Scene focus control"
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
    >
      <span className="scene-blur-control__label scene-blur-control__label--clear">
        How
        <br />
        Humans Sense
        <br />
        the World
      </span>
      <div
        className="scene-blur-control__track"
        style={{ "--scene-blur-thumb-position": `${100 - normalizedValue}%` }}
      >
        <span className="scene-blur-control__endpoint scene-blur-control__endpoint--top" aria-hidden="true" />
        <span className="scene-blur-control__endpoint scene-blur-control__endpoint--bottom" aria-hidden="true" />
        <input
          className="scene-blur-control__range"
          type="range"
          min="0"
          max="100"
          step="1"
          value={normalizedValue}
          aria-label="Scene blur amount"
          aria-valuetext={`${normalizedValue}% blur`}
          onChange={(event) => {
            dismissSwipeHint();
            onChange?.(Number(event.target.value));
          }}
          style={{ "--scene-blur-progress": `${normalizedValue}%` }}
        />
        <output
          className="scene-blur-control__value"
          tabIndex={0}
          style={{
            "--scene-blur-thumb-position": `${100 - normalizedValue}%`,
          }}
          onPointerDown={(event) => {
            handleMetricPointerDown(event);
          }}
          onPointerMove={handleMetricPointerMove}
          onPointerUp={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
        >
          <strong>{normalizedValue}%</strong>
          <span>
            Spatial
            <br />
            Intelligence
          </span>
        </output>
        <span
          className="scene-blur-control__state-group"
          role="slider"
          tabIndex={0}
          aria-label="Scene blur amount"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={normalizedValue}
          aria-valuetext={`${normalizedValue}% blur`}
          onPointerDown={handleMetricPointerDown}
          onPointerMove={handleMetricPointerMove}
          onPointerUp={handleMetricPointerUp}
          onPointerCancel={handleMetricPointerUp}
          onKeyDown={handleMetricKeyDown}
        >
          <span className="scene-blur-control__state" aria-live="polite">
            {spatialStageWords.map((word, index) => {
              if (spatialStageWords[index - 1] === "&") return null;
              const line = word === "&" && spatialStageWords[index + 1]
                ? `${word} ${spatialStageWords[index + 1]}`
                : word;
              return <span key={`${line}-${index}`}>{line}</span>;
            })}
          </span>
          {showSwipeHint && (
            <span className="scene-blur-control__swipe-hint" aria-hidden="true">
              <span>↑</span>
              <span>↑</span>
              <span>↑</span>
            </span>
          )}
        </span>
      </div>
      <span className="scene-blur-control__label scene-blur-control__label--blur">
        How
        <br />
        AI Senses
        <br />
        the World
      </span>
      <button
        type="button"
        className="scene-blur-control__toggle scene-blur-control__toggle--collapse"
        aria-label="Collapse scene focus control"
        aria-expanded="true"
        onClick={() => {
          if (externallyCollapsed) return;
          setIsManuallyCollapsed(true);
        }}
      >
        <span className="scene-blur-control__toggle-symbol scene-blur-control__toggle-symbol--minus" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="scene-blur-control__toggle scene-blur-control__toggle--open"
        aria-label="Open scene focus control"
        aria-expanded="false"
        onClick={() => {
          if (externallyCollapsed) return;
          setIsManuallyCollapsed(false);
        }}
      >
        <span className="scene-blur-control__toggle-symbol" aria-hidden="true">+</span>
      </button>
    </div>
  );
}
